import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { portalApi } from "../services/portalApi";

const PortalAuthContext = createContext(null);

const STORAGE_KEY = "portal_token";
const USER_KEY = "portal_user";

export function PortalAuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY));
  /** Sin token en LS el arranque ya es válido; con token hay que validar /auth/me antes de confiar en la sesión. */
  const [authReady, setAuthReady] = useState(
    () => !localStorage.getItem(STORAGE_KEY)
  );

  const persist = useCallback((nextToken, nextUser) => {
    if (nextToken) {
      localStorage.setItem(STORAGE_KEY, nextToken);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    if (nextUser) {
      localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
    } else {
      localStorage.removeItem(USER_KEY);
    }
    setToken(nextToken || null);
    setUser(nextUser || null);
  }, []);

  const login = useCallback(
    async (email, password) => {
      const data = await portalApi.login({ email, password });
      persist(data.token, data.user);
      return data;
    },
    [persist]
  );

  const register = useCallback(
    async (payload) => {
      const data = await portalApi.register(payload);
      persist(data.token, data.user);
      return data;
    },
    [persist]
  );

  const logout = useCallback(async () => {
    try {
      await portalApi.logout();
    } catch {
      // best-effort — clear local session regardless
    } finally {
      persist(null, null);
    }
  }, [persist]);

  const refreshMe = useCallback(async () => {
    const storedToken = localStorage.getItem(STORAGE_KEY);
    if (!storedToken) return null;
    const data = await portalApi.me();
    setUser(data.user);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    return data.user;
  }, []);

  useEffect(() => {
    const t = localStorage.getItem(STORAGE_KEY);
    if (!t) return;
    refreshMe()
      .catch((err) => {
        // 401 = invalid/expired token; 404 = account deleted — clear session in both cases
        if (err?.status === 401 || err?.status === 404) {
          persist(null, null);
        }
      })
      .finally(() => setAuthReady(true));
  }, [persist, refreshMe]);

  const value = useMemo(
    () => ({
      user,
      token,
      authReady,
      isAuthenticated: Boolean(token && user && authReady),
      login,
      register,
      logout,
      refreshMe,
    }),
    [user, token, authReady, login, register, logout, refreshMe]
  );

  return (
    <PortalAuthContext.Provider value={value}>{children}</PortalAuthContext.Provider>
  );
}

export function usePortalAuth() {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) {
    throw new Error("usePortalAuth debe usarse dentro de PortalAuthProvider");
  }
  return ctx;
}
